import { z } from 'zod'
import { SCREENING_NODE_TYPES } from '../config/contact-screening.config'
import { FlowNodeTypes } from '../types'

const leaderProfileSchema = z.strictObject({
  version: z.literal(1),
  engine: z.literal('jev'),
  fault_policy: z.enum(['gemma_autonomous', 'safe_close']),
  scribe_close_permit: z.boolean(),
  amd: z.literal('off'),
})
export type LeaderProfile = z.infer<typeof leaderProfileSchema>

export const LEADER_PROFILE_V1: LeaderProfile = {
  version: 1,
  engine: 'jev',
  fault_policy: 'gemma_autonomous',
  scribe_close_permit: true,
  amd: 'off',
}

export const SYSTEM_ONE_THINKING_INVALID =
  'Saved System One Thinking settings are invalid. Switch it off to remove them.'

interface AnyNode {
  type?: string
  data?: { config?: unknown } | null
}

// An old V2 node keeps the profile it was saved with: on never gives it one, off clears it.
const SWITCHABLE = new Set<string>([FlowNodeTypes.ai_agent_v29, ...SCREENING_NODE_TYPES])
const CARRIERS = new Set<string>([FlowNodeTypes.ai_agent_v2, ...SWITCHABLE])

const configOf = (node: AnyNode) => (node.data?.config ?? {}) as Record<string, unknown>
const profileOf = (node: AnyNode) =>
  CARRIERS.has(node.type ?? '') ? configOf(node).leader_profile : undefined
const isLeaderProfile = (value: unknown): value is LeaderProfile =>
  leaderProfileSchema.safeParse(value).success

export const isSwitchable = (node: AnyNode) => SWITCHABLE.has(node.type ?? '')

// Nodes saved with different profiles read as the first valid one; the next save makes them agree.
export const savedLeaderProfile = (nodes: AnyNode[]): LeaderProfile | null =>
  nodes.map(profileOf).find(isLeaderProfile) ?? null

export const hasInvalidLeaderProfile = (nodes: AnyNode[]) =>
  nodes.map(profileOf).some((profile) => profile != null && !isLeaderProfile(profile))

export const stampLeaderProfile = <T extends AnyNode>(nodes: T[], profile: LeaderProfile | null) =>
  nodes.map((node): T => {
    if (!(profile ? SWITCHABLE : CARRIERS).has(node.type ?? '')) return node
    const { leader_profile: _, ...config } = configOf(node)
    const stamped = profile ? { ...config, leader_profile: profile } : config
    return { ...node, data: { ...node.data, config: stamped } }
  })

// Null stops the save: an invalid saved profile is never dropped without a click on the switch.
export const leaderNodesForSave = <T extends AnyNode>(nodes: T[], profile: LeaderProfile | null) =>
  hasInvalidLeaderProfile(nodes) ? null : stampLeaderProfile(nodes, profile)
